import classNames from "classnames";
import * as React from "react";

import { DeletableBox } from "../DeletableBox";

import styles from "./MetadataView.module.css";
import { Metadata, MetadataDiff } from "./types";
import { Value } from "./Value";

// Lazy so monaco-editor (loaded by JsonMetadataEditor) is not evaluated at
// module-load time.
const JsonMetadataEditor = React.lazy(() =>
  import("./JsonMetadataEditor").then((m) => ({
    default: m.JsonMetadataEditor,
  })),
);

type MetadataChangeHandler = (metadata: Metadata, diff?: MetadataDiff) => void;

interface BaseProps {
  metadata: Metadata;
  onDeleteMetadata?: (key: string) => void;
  className?: classNames.Argument;
  jsonMetadataDisabled?: boolean;
}

/**
 * `onChange` is required unless the caller declares the view read-only with a
 * literal `disabled`. Omitting it used to compile, which is how the device
 * panel shipped an editor whose Save was silently discarded; the compiler now
 * makes a caller choose. `disabled` may still be computed at runtime — that
 * only forces the caller into the editable arm, where a handler is mandatory.
 */
type Props = BaseProps &
  (
    | { onChange: MetadataChangeHandler; disabled?: boolean }
    | { onChange?: never; disabled: true }
  );

export const MetadataView: React.FC<Props> = ({
  metadata,
  onChange,
  onDeleteMetadata,
  className,
  disabled = false,
  jsonMetadataDisabled = false,
}) => {
  function removeKeyFromMetadata(key: string) {
    const copy = { ...metadata };
    delete copy[key];

    if (onChange) {
      onChange(copy, { keysDeleted: [key] });
    }

    if (onDeleteMetadata) {
      onDeleteMetadata(key);
    }
  }

  const hasEntries = Object.keys(metadata).length > 0;

  return (
    <dl
      className={classNames(
        styles.root,
        hasEntries && styles.populated,
        className,
      )}
    >
      <div className={styles.grid}>
        {Object.keys(metadata)
          .sort((a, b) => {
            return a.localeCompare(b);
          })
          .map((mKey) => {
            const onDelete = () => {
              if (!disabled) {
                removeKeyFromMetadata(mKey);
              }
            };

            const value = metadata[mKey];

            let content = (
              <>
                <dt className={styles.key}>{mKey}</dt>
                <dd className={styles.value}>
                  <Value value={value} />
                </dd>
              </>
            );
            // Pardon the double-negatives, but it makes
            // for better parameter names on the outside.
            const enableEditing = !jsonMetadataDisabled && !disabled;
            if (enableEditing) {
              // Allow editing a non-disabled box.
              content = (
                <React.Suspense fallback={content}>
                  <JsonMetadataEditor
                    value={value}
                    // Pass through as undefined when there is nothing to call.
                    // JsonMetadataEditor only wires up its click handler when
                    // this prop is set, so a caller that omits `onChange` gets
                    // inert text rather than a dialog whose Save is discarded.
                    onObjectValueChanged={
                      onChange
                        ? (newValue) =>
                            onChange(
                              { ...metadata, [mKey]: newValue },
                              { keysUpdated: [mKey] },
                            )
                        : undefined
                    }
                    // The Suspense fallback above repeats `content` verbatim,
                    // so this marker is what distinguishes the mounted editor
                    // from the still-loading chunk. Keyed per entry: one editor
                    // renders per metadata key.
                    triggerTestId={`metadata-editor-trigger-${mKey}`}
                  >
                    {content}
                  </JsonMetadataEditor>
                </React.Suspense>
              );
            }
            return (
              <div key={mKey} className={styles.item}>
                <DeletableBox onDelete={onDelete} disabled={disabled}>
                  {content}
                </DeletableBox>
              </div>
            );
          })}
      </div>
    </dl>
  );
};
